// 총괄 교부금 결재 문서함
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import Button from "@/js/common/ui/Button";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import TextBox from "@/js/common/ui/input/TextBox";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import BgtAtrzRtsoOvrlExpndDialog from "../dialog/BgtAtrzRtsoOvrlExpndDialog";
import BgtAtrzRtsoOvrlDpstDialog from "../dialog/BgtAtrzRtsoOvrlDpstDialog";
import { fldInfsProps } from "@/js/common/ui/GridTable";

type DocMode = "" | "DPST" | "EXPND";

interface Props {
  processing: boolean;
  setProcessing: Function;
}

const AtrzDocBoxSumTab = ({processing, setProcessing}: Props) => {
  const [ovrlKeyword, setOvrlKeyword] = React.useState("");
  const [ovrlSttDt, setOvrlSttDt] = React.useState("");
  const [ovrlEndDt, setOvrlEndDt] = React.useState("");

  const [feeArr, setFeeArr] = React.useState<any[]>([]);
  const [callTime, setCallTime] = React.useState<Date>(new Date());

  const [selectedAtrzId, setSelectedAtrzId] = React.useState("");

  const [mode, setMode] = React.useState<DocMode>("");
  const [atrzLineId, setAtrzLineId] = React.useState("");
  const [atrzId, setAtrzId] = React.useState("");

  const [_itemAprvr, setItemAprvr] = React.useState<any>({});
  const [listLine, setListLine] = React.useState<any[]>([]);

  // 지출
  const [listExpnd, setListExpnd] = React.useState<any[]>([]);
  const [sumExpnd, setSumExpnd] = React.useState<any>({});
  const [ovrlMbsdsExpndDocId, setOvrlMbsdsExpndDocId] = React.useState("");

  // 수입
  const [listDpst, setListDpst] = React.useState<any[]>([]);
  const [sumDpst, setSumDpst] = React.useState<any>({});
  const [ovrlMbsdsDpstDocId, setOvrlMbsdsDpstDocId] = React.useState("");

  const clearDetail = () => {
    setSelectedAtrzId("");
    setMode("");
    setAtrzId("");
    setAtrzLineId("");
    setItemAprvr({});
    setListLine([]);

    setListExpnd([]);
    setSumExpnd({});
    setOvrlMbsdsExpndDocId("");

    setListDpst([]);
    setSumDpst({});
    setOvrlMbsdsDpstDocId("");
  };

  // ---------------------------
  // 결재 대기 목록 조회
  // ---------------------------
  React.useEffect(() => {
    // 신청일자 검색 조건 확인
    if (Util.isNotEmpty(ovrlSttDt) && Util.isNotEmpty(ovrlEndDt)) {
        if (ovrlSttDt > ovrlEndDt) {
            AppContext.openAlert({ 
                title: "신청일자 검색 조건 확인", 
                msg: "신청일자의 검색 시작일자가 종료일자보다 느립니다.", 
                icon: "warning" 
            }); 
            setOvrlEndDt("")
            return;
        } 
    }
    AppContext.showSpinner();

    Axios.dataAccess({
      url: "system/atrz/ovrl/select-aprvr-ovrl-list.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        sttDt: ovrlSttDt,
        endDt: ovrlEndDt,
      },
      onSuccessFn: (res: any) => {
        setFeeArr(res.result.list);
        AppContext.hideSpinner();
      },
    });
  }, [ovrlSttDt, ovrlEndDt, callTime]);

  const fldInfs: fldInfsProps[] = [
    { type: "seq", width:  50, headAlign: "center", dataAlign: "center", label: "No." },
    { type: "text", fldNm: "atrzDocSeNm", width: 140, headAlign: "center", dataAlign: "center", label: "문서 구분" },
    { type: "text", fldNm: "atrzTtlCn", width: 320, headAlign: "center", dataAlign: "left", label: "제목" },
    { type: "text", fldNm: "aplyOrgNm", width: 160, headAlign: "center", dataAlign: "center", label: "신청 조직" },
    { type: "text", fldNm: "atrzAmt", width: 140, headAlign: "center", dataAlign: "right", label: "금액" },
    { type: "text", fldNm: "userNm", width: 120, headAlign: "center", dataAlign: "center", label: "신청자" },
    { type: "date", fldNm: "atrzReqDt", width: 120, headAlign: "center", dataAlign: "center", label: "신청일" },
    { type: "text", fldNm: "atrzSttsNm", width:  90, headAlign: "center", dataAlign: "center", label: "상태" },
  ];

  const filteredFeeArr = React.useMemo(() => {
    return Util.filterTwoOrFieldData(feeArr, fldInfs, "atrzTtlCn", ovrlKeyword, "userNm", ovrlKeyword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeArr, ovrlKeyword]);

  // ---------------------------
  // 상세 조회 (지출)
  // - initFirst=true: "결재 대기 목록 클릭" -> 무조건 1번째 선택
  // - initFirst=false: 재조회 -> 현재 선택 유지(있으면 유지, 없으면 그대로 둠)
  // ---------------------------
  const fetchExpndDetail = (id: string, lineId: string, initFirst: boolean) => {
    if (Util.isEmpty(id)) return;

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/atrz/ovrl/select-aprvr-ovrl-expnd-list.do",
      methodType: "post",
      paramType: "json",
      paramData: { atrzId: id, atrzLineId: lineId },
      onSuccessFn: (res: any) => {
        const listLineRes = res.result.listLine;
        const list = res.result.list;
        const sum = res.result.listSum;

        setMode("EXPND");
        setAtrzId(id);
        setAtrzLineId(lineId);
        setItemAprvr(res.result.itemAprvr);
        setListLine(listLineRes);

        setListExpnd(list);
        setSumExpnd(sum);

        if (Array.isArray(list) && list.length > 0) {
          if (initFirst) {
            // "클릭 진입"은 무조건 첫 행 선택
            const firstId = String(list[0].atrzLinkDocId || list[0].ovrlMbsdsExpndDocId);
            setOvrlMbsdsExpndDocId(firstId);
          } else {
            // 재조회는 현재 선택 유지(목록에 있으면)
            const cur = String(ovrlMbsdsExpndDocId);
            const exists = list.some(
              (r: any) => String(r.atrzLinkDocId || r.ovrlMbsdsExpndDocId) === cur
            );
            if (exists) setOvrlMbsdsExpndDocId(cur);
          }
        }

        // 모드 전환 시 반대 리스트 정리
        setListDpst([]);
        setSumDpst({});
        setOvrlMbsdsDpstDocId("");

        AppContext.hideSpinner();
      },
    });
  };

  // ---------------------------
  // 상세 조회 (수입)
  // ---------------------------
  const fetchDpstDetail = (id: string, lineId: string, initFirst: boolean) => {
    if (Util.isEmpty(id)) return;

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/atrz/ovrl/select-aprvr-ovrl-dpst-list.do",
      methodType: "post",
      paramType: "json",
      paramData: { atrzId: id, atrzLineId: lineId },
      onSuccessFn: (res: any) => {
        const listLineRes = res.result.listLine;
        const list = res.result.list;
        const sum = res.result.listSum;

        setMode("DPST");
        setAtrzId(id);
        setAtrzLineId(lineId);

        setItemAprvr(res.result.itemAprvr);
        setListLine(listLineRes);

        setListDpst(list);
        setSumDpst(sum);

        if (Array.isArray(list) && list.length > 0) {
          if (initFirst) {
            const firstId = String(list[0].atrzLinkDocId || list[0].ovrlMbsdsDpstDocId);
            setOvrlMbsdsDpstDocId(firstId);
          } else {
            const cur = String(ovrlMbsdsDpstDocId);
            const exists = list.some(
              (r: any) => String(r.atrzLinkDocId || r.ovrlMbsdsDpstDocId) === cur
            );
            if (exists) setOvrlMbsdsDpstDocId(cur);
          }
        }

        // 모드 전환 시 반대 리스트 정리
        setListExpnd([]);
        setSumExpnd({});
        setOvrlMbsdsExpndDocId("");

        AppContext.hideSpinner();
      },
    });
  };

  // callTime 재조회 시: 현재 모드 상세 재조회(선택 유지)
  React.useEffect(() => {
    if (Util.isEmpty(mode) || Util.isEmpty(atrzId) || Util.isEmpty(atrzLineId)) return;

    if (mode === "EXPND") fetchExpndDetail(atrzId, atrzLineId, false);
    if (mode === "DPST") fetchDpstDetail(atrzId, atrzLineId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callTime]);

  // ---------------------------
  // 결재 대기 목록 클릭
  // -> initFirst=true (무조건 첫 행 선택)
  // ---------------------------
  const selectDoc = (row: any) => {
    const id = String(row.atrzId);
    const docSeCd = String(row.atrzDocSeCd);
    const lineId = String(row.atrzLineId);

    if (Util.isEmpty(id) || Util.isEmpty(docSeCd)) return;

    setSelectedAtrzId(id);

    if (docSeCd === "A02") {
      fetchExpndDetail(id, lineId, true);
      return;
    }

    if (docSeCd === "A01") {
      fetchDpstDetail(id, lineId, true);
      return;
    }

    clearDetail();
  };

  // ---------------------------
  // 처리 후 다음 결의서 자동 선택 (DB순서 그대로)
  // - 다음이 있으면 next로
  // - 마지막이면 그대로 유지
  // ---------------------------
  const moveNextDoc = React.useCallback(() => {
    if (mode === "DPST") {
      if (!Array.isArray(listDpst) || listDpst.length === 0) return;

      const curId = String(ovrlMbsdsDpstDocId);
      const curIdx = listDpst.findIndex(
        (r: any) => String(r.atrzLinkDocId || r.ovrlMbsdsDpstDocId) === curId
      );

      if (curIdx < 0) return;
      if (curIdx === listDpst.length - 1) return; 

      const nextId = String(listDpst[curIdx + 1].atrzLinkDocId || listDpst[curIdx + 1].ovrlMbsdsDpstDocId);
      setOvrlMbsdsDpstDocId(nextId);
      return;
    }

    if (mode === "EXPND") {
      if (!Array.isArray(listExpnd) || listExpnd.length === 0) return;

      const curId = String(ovrlMbsdsExpndDocId);
      const curIdx = listExpnd.findIndex(
        (r: any) => String(r.atrzLinkDocId || r.ovrlMbsdsExpndDocId) === curId
      );

      if (curIdx < 0) return;
      if (curIdx === listExpnd.length - 1) return;

      const nextId = String(listExpnd[curIdx + 1].atrzLinkDocId || listExpnd[curIdx + 1].ovrlMbsdsExpndDocId);
      setOvrlMbsdsExpndDocId(nextId);
    }
  }, [mode, listDpst, listExpnd, ovrlMbsdsDpstDocId, ovrlMbsdsExpndDocId]);

  // ---------------------------
  // 결재/반려 로직(기존 유지)
  // ---------------------------
  const approve = () => {
    if (processing) return;
    if (Util.isEmpty(atrzId) || Util.isEmpty(mode)) return;

    const list = mode === "DPST" ? listDpst : listExpnd;
    const hasInvalid = list.some((row: any) => String(row.atrzLinkDocIdntySeCd) !== "01");

    if (hasInvalid) {
      AppContext.openAlert({
        title: "결재 불가",
        msg: "확인되지 않은 목록이 있어 결재를 진행할 수 없습니다.",
        icon: "warning",
      });
      return;
    }

    const url =
      mode === "EXPND"
        ? "system/atrz/ovrl/process-aprvr-ovrl-expnd-atrz.do"
        : "system/atrz/ovrl/process-aprvr-ovrl-dpst-atrz.do";

    AppContext.openAlert({
      title: "결재 처리",
      msg: "해당 문서를 결재 처리하시겠습니까?",
      icon: "check",
      confirmText: "결재",
      closeText: "취소",
      handleConfirm: () => {
        setProcessing(true);
        AppContext.showSpinner();

        Axios.dataAccess({
          url,
          methodType: "post",
          paramType: "json",
          paramData: { atrzId },
          onSuccessFn: () => {
            AppContext.hideSpinner();
            setProcessing(false);

            AppContext.openAlert({
              title: "알림",
              msg: "결재가 완료되었습니다.",
              icon: "info",
            });

            clearDetail();
            setCallTime(new Date());
          },
        });
      },
    });
  };

  const rejectAll = () => {
    if (processing) return;

    if (Util.isEmpty(atrzId) || Util.isEmpty(mode)) {
      AppContext.openAlert({ title: "알림", msg: "반려할 문서를 선택해주세요.", icon: "warning" });
      return;
    }
    if (Util.isEmpty(atrzLineId)) {
      AppContext.openAlert({ title: "알림", msg: "결재 정보(atrzLineId)가 없습니다.", icon: "warning" });
      return;
    }

    const list = mode === "DPST" ? listDpst : listExpnd;
    const hasNullIdnty = list.some((row: any) => Util.isEmpty(row.atrzLinkDocIdntySeCd));
    if (hasNullIdnty) {
      AppContext.openAlert({
        title: "반려 불가",
        msg: "확인되지 않은 내역이 있어 반려할 수 없습니다.",
        icon: "warning",
      });
      return;
    }

    const url =
      mode === "EXPND"
        ? "system/atrz/ovrl/process-aprvr-ovrl-expnd-rjct.do"
        : "system/atrz/ovrl/process-aprvr-ovrl-dpst-rjct.do";

    AppContext.openAlert({
      title: "반려 처리",
      msg: "해당 문서를 반려 처리하시겠습니까?",
      icon: "check",
      confirmText: "반려",
      closeText: "취소",
      handleConfirm: () => {
        setProcessing(true);
        AppContext.showSpinner();

        Axios.dataAccess({
          url,
          methodType: "post",
          paramType: "json",
          paramData: {
            atrzId: String(atrzId),
            atrzLineId: String(atrzLineId),
          },
          onSuccessFn: () => {
            AppContext.hideSpinner();
            setProcessing(false);
            AppContext.openAlert({
              title: "알림",
              msg: "반려가 처리되었습니다.",
              icon: "info",
            });

            clearDetail();
            setCallTime(new Date());
          },
        });
      },
    });
  };

  const showDetail = Util.isNotEmpty(atrzId) && Util.isNotEmpty(mode);

  // Stepper 표시용(기존 유지)
  const _lines = Array.isArray(listLine) ? listLine : [];
  const _MAX = 5;
  const _cur = _lines.findIndex((x: any) => String(x.atrzPrgrsSttsCd) === "02");

  let lineView: any[] = [];
  if (_cur < 0) lineView = _lines.slice(0, _MAX);
  else {
    let start = _cur - Math.floor(_MAX / 2);
    if (start < 0) start = 0;
    if (start > _lines.length - _MAX) start = Math.max(0, _lines.length - _MAX);
    lineView = _lines.slice(start, start + _MAX);
  }

  const selectExpndRow = (row: any) => {
    const id = String(row.atrzLinkDocId || row.ovrlMbsdsExpndDocId);
    setOvrlMbsdsExpndDocId(id);
  };

  const selectDpstRow = (row: any) => {
    const id = String(row.atrzLinkDocId || row.ovrlMbsdsDpstDocId);
    setOvrlMbsdsDpstDocId(id);
  };

  const canReject =
    showDetail &&
    (mode === "DPST" ? listDpst : listExpnd).some((row: any) => row.atrzLinkDocIdntySeCd === "02");

  const clearForm = () => {
    setOvrlKeyword("");
    setOvrlSttDt("");
    setOvrlEndDt("");
  };

  return (
    <div>
      {/* 검색 영역 */}
      <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
        <div className="dpFlx aiC flxWrap gap14">
          <div className="dpFlx aiC">
            <span className="w60 fm-c fs15 fc333333 ls1 taL">검색어</span>
            <TextBox
              value={ovrlKeyword}
              width={240}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              style={{ backgroundColor: "#fff", padding: "0 10px" }}
              placeholder="제목 또는 신청자"
              onChangeFunc={(data: string) => setOvrlKeyword(data)}
              onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter") setCallTime(new Date());
              }}
            />
          </div>

          <div className="dpFlx aiC">
            <span className="w70 fm-c fs15 fc333333 ls1 taL">신청일자</span>
            <DatePickerBox
              width={150}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              bgColor="white"
              textAlign="left"
              calendarThemeColor={AppContext.themeColor}
              className="mr5"
              value={ovrlSttDt}
              onChange={(data: string) => setOvrlSttDt(data)}
            />
            <span className="fm-c fc666666 mr5">~</span>
            <DatePickerBox
              width={150}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              bgColor="white"
              textAlign="left"
              calendarThemeColor={AppContext.themeColor}
              className="mr5"
              value={ovrlEndDt}
              onChange={(data: string) => setOvrlEndDt(data)}
            />
          </div>

          <div className="dpFlx aiC mlA gap8">
            <Button
              width={70}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              variant="styInitBtn"
              borderRadius={5}
              buttonClass="dpFlx aiC jcC"
              buttonName="초기화"
              onClickFunc={clearForm}
            />
          </div>
        </div>
      </div>

      {/* 결재 대기 목록 */}
      <div className="bd1d9d9d9 bdrd6 bgcWhite mb15">
        <div className="p15">
          <div className="dpFlx aiC jcSb mb10 h44">
            <div className="fm-b fs16 fc007bff ls1 fw700">총괄 교부금 결재 대기 목록</div>

            {showDetail && (
              <div className="dpFlx gap8">
                {canReject && (
                  <Button
                    width={110}
                    height={36}
                    fontFamily="fm-b"
                    fontSize={14}
                    letterSpacing={-1}
                    variant="styDeleteBtn"
                    borderRadius={6}
                    buttonClass="dpFlx aiC jcC"
                    buttonName="반려처리"
                    onClickFunc={rejectAll}
                    disabled={processing}
                  />
                )}

                <Button
                  width={110}
                  height={36}
                  fontFamily="fm-b"
                  fontSize={14}
                  letterSpacing={-1}
                  variant="styCompleteBtn"
                  borderRadius={6}
                  buttonClass="dpFlx aiC jcC"
                  buttonName={processing ? "처리중..." : "결재처리"}
                  onClickFunc={approve}
                  disabled={canReject || processing}
                />
              </div>
            )}
          </div>

          <div className="bd1 bdcEDEDED bdrd6" style={{ overflow: "hidden" }}>
            <div style={{ maxHeight: 1000, overflow: "auto" }}>
              <table className="w100p" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
                <colgroup>
                  <col style={{ width: 54 }} />
                  <col style={{ width: 140 }} />
                  <col style={{ width: 320 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 140 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 90 }} />
                </colgroup>
                <thead>
                  <tr className="bgcf0f8ff">
                    <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw" style={{ width: 54 }}>
                      선택
                    </th>
                    <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw" style={{ width: 140 }}>
                      문서 구분
                    </th>
                    <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw" style={{ width: 320 }}>
                      제목
                    </th>
                    <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw" style={{ width: 160 }}>
                      신청 조직
                    </th>
                    <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw" style={{ width: 140 }}>
                      금액
                    </th>
                    <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw" style={{ width: 120 }}>
                      신청자
                    </th>
                    <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw" style={{ width: 120 }}>
                      신청일
                    </th>
                    <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw" style={{ width: 90 }}>
                      상태
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredFeeArr.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p20 taC fm-c fs13 fc666666">
                        조회된 데이터가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    filteredFeeArr.map((row: any) => {
                      const id = String(row.atrzId);
                      const isSelected = selectedAtrzId === id;

                      return (
                        <tr
                          key={id}
                          onClick={() => selectDoc(row)}
                          className="transitionBgc"
                          style={{ cursor: "pointer", backgroundColor: isSelected ? "#eaf1faea" : "transparent" }}
                        >
                          <td className="bd1 bdcEDEDED p10 taC">
                            <input
                              type="radio"
                              name="feeRowSelect"
                              checked={isSelected}
                              onChange={() => selectDoc(row)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>

                          <td className="bd1 bdcEDEDED p10 taC fm-c fs13 fc333333 wsNw">{row.atrzDocSeNm}</td>
                          <td className="bd1 bdcEDEDED p10 taL fm-c fs13 fc333333 ofH toE">{row.atrzTtlCn}</td>
                          <td className="bd1 bdcEDEDED p10 taC fm-c fs13 fc333333 wsNw">{row.aplyOrgNm}</td>
                          <td className="bd1 bdcEDEDED p10 taR fm-c fs13 fc333333 wsNw">
                            {Util.isEmpty(row.atrzAmt) ? "" : Util.comma(String(row.atrzAmt))}
                          </td>
                          <td className="bd1 bdcEDEDED p10 taC fm-c fs13 fc333333 wsNw">{row.userNm}</td>
                          <td className="bd1 bdcEDEDED p10 taC fm-c fs13 fc333333 wsNw">
                            {Util.cvtDateStrForm(String(row.atrzReqDt), "-", "YYYYMMDD")}
                          </td>
                          <td className="bd1 bdcEDEDED p10 taC fm-c fs13 fc333333 wsNw">{row.atrzSttsNm}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* 상세 */}
      {!showDetail ? null : (
        <div>
          <div className="mb10">
            <div className="fm-b fs16 fc007bff ls1 fw700 mb10">결재진행</div>

            <div className="bd1d9d9d9 bdrd6 bgcWhite">
              <div className="p12">
                {lineView.length === 0 ? (
                  <div className="p20 taC fm-c fs13 fc666666">결재 라인 정보가 없습니다.</div>
                ) : (
                  <Stepper
                    activeStep={Math.max(0, lineView.findIndex((x: any) => String(x.atrzPrgrsSttsCd) === "02"))}
                    alternativeLabel
                    sx={{
                      paddingTop: 1,
                      paddingBottom: 0.5,
                      "& .MuiStepLabel-label": { fontFamily: "fm-c", fontSize: 13, letterSpacing: "-0.5px" },
                      "& .MuiStepLabel-label.Mui-active": { fontFamily: "fm-b", color: "#333333" },
                      "& .MuiStepLabel-label.Mui-completed": { color: "#333333" },
                      "& .MuiStepIcon-root": { fontSize: 22 },
                      "& .MuiStepIcon-root.Mui-active": { color: AppContext.themeColor },
                      "& .MuiStepConnector-line": { borderTopWidth: 2, borderColor: "#e0e0e0" },
                    }}
                  >
                    {lineView.map((x: any, idx: number) => (
                      <Step key={`${String(x.atrzSeq)}-${idx}`}>
                        <StepLabel
                          optional={
                            <div style={{ lineHeight: 1.2 }}>
                              <div
                                className="fm-c fs12 fc777777 wsNw ofH toE"
                                style={{ display: "block", maxWidth: 220, margin: "0 auto" }}
                                title={String(x.aprvrNm)}
                              >
                                {String(x.aprvrNm)}
                              </div>
                              <div
                                className="fm-c fs12 fc777777 wsNw ofH toE"
                                style={{ display: "block", maxWidth: 220, margin: "2px auto 0" }}
                                title={String(x.atrzDt)}
                              >
                                {String(x.atrzDt ?? "")}
                              </div>
                            </div>
                          }
                        >
                          {String(x.atrzPrgrsSttsNm)}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                )}
              </div>
            </div>
          </div>

          <div className="dpFlx gap15" style={{ flexWrap: "nowrap" }}>
            <div style={{ width: 420 }}>
              <div className="bd1d9d9d9 bdrd6 bgcWhite">
                <div className="p10">

                  {mode === "DPST" ? (
                    <BgtAtrzRtsoOvrlDpstDialog
                      ovrlMbsdsDpstDocId={ovrlMbsdsDpstDocId}
                      atrzId={atrzId}
                      atrzLineId={atrzLineId}
                      setCallTime={setCallTime}
                      onDone={moveNextDoc}
                    />
                  ) : (
                    <BgtAtrzRtsoOvrlExpndDialog
                      ovrlMbsdsExpndDocId={ovrlMbsdsExpndDocId}
                      atrzId={atrzId}
                      atrzLineId={atrzLineId}
                      setCallTime={setCallTime}
                      onDone={moveNextDoc}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="flx1">
              <div className="dpFlx aiC jcSb mb10 pt20">
                <div className="fm-b fs16 fc007bff ls1 fw700">{mode === "DPST" ? "수입결의서 목록" : "지출결의서 목록"}</div>
              </div>

              <div className="bd1d9d9d9 bdrd6 bgcWhite w763">
                <div className="p10">
                  <div className="bd1 bdcEDEDED bdrd6" style={{ overflow: "hidden" }}>
                    <div style={{ maxHeight: 500, overflowY: "auto", overflowX: "hidden" }}>
                      {mode === "DPST" ? (
                        <table className="w100p" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
                          <colgroup>
                            <col style={{ width: 54 }} />
                            <col style={{ width: 110 }} />
                            <col style={{ width: 110 }} />
                            <col style={{ width: 90 }} />
                            <col style={{ width: 160 }} />
                            <col style={{ width: 139 }} />
                            <col style={{ width: 80 }} />
                          </colgroup>

                          <thead>
                            <tr className="bgcf0f8ff">
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">선택</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">수입일자</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">관</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">항</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">목</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">금액</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">상태</th>
                            </tr>
                          </thead>

                          <tbody>
                            {listDpst.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="p20 taC fm-c fs13 fc666666">
                                  조회된 데이터가 없습니다.
                                </td>
                              </tr>
                            ) : (
                              listDpst.map((row: any) => {
                                const rawId = row.atrzLinkDocId || row.ovrlMbsdsDpstDocId;
                                const id = String(rawId);
                                const isSelected = ovrlMbsdsDpstDocId === String(rawId);

                                return (
                                  <tr
                                    key={id}
                                    onClick={() => selectDpstRow(row)}
                                    className="transitionBgc"
                                    style={{ cursor: "pointer", backgroundColor: isSelected ? "#eaf1faea" : "transparent" }}
                                  >
                                    <td className="bd1 bdcEDEDED p10 taC">
                                      <input
                                        type="radio"
                                        name="dpstRowSelect"
                                        checked={isSelected}
                                        onChange={() => selectDpstRow(row)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taC fm-c fs13 fc333333 wsNw">
                                      {Util.cvtDateStrForm(String(row.dpstDt), "-", "YYYYMMDD")}
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taL fm-c fs13 fc333333 wsNw ofH toE" title={String(row.bgtSbjct1stNm)}>
                                      {row.bgtSbjct1stNm}
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taL fm-c fs13 fc333333 wsNw ofH toE" title={String(row.bgtSbjct2stNm)}>
                                      {row.bgtSbjct2stNm}
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taL fm-c fs13 fc333333 wsNw ofH toE" title={String(row.bgtSbjct3stNm)}>
                                      {row.bgtSbjct3stNm}
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taR fm-c fs13 fc333333 wsNw">{Util.comma(String(row.dpstAmt))}</td>
                                    <td
                                      className="bd1 bdcEDEDED p10 taC fm-c fs13 wsNw"
                                      style={{
                                        color:
                                          String(row.atrzLinkDocIdntySeCd) === "01"
                                            ? "#007bff"
                                            : String(row.atrzLinkDocIdntySeCd) === "02"
                                            ? "#dc3545"
                                            : "#333333",
                                      }}
                                    >
                                      {row.atrzLinkDocIdntySeNm}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      ) : (
                        <table className="w100p" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
                          <colgroup>
                            <col style={{ width: 54 }} />
                            <col style={{ width: 110 }} />
                            <col style={{ width: 110 }} />
                            <col style={{ width: 90 }} />
                            <col style={{ width: 160 }} />
                            <col style={{ width: 139 }} />
                            <col style={{ width: 80 }} />
                          </colgroup>

                          <thead>
                            <tr className="bgcf0f8ff">
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">선택</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">행위년월일</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">관</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">항</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">목</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">금액</th>
                              <th className="bd1 bdcEDEDED p10 taC fm-b fs13 fc333333 wsNw">상태</th>
                            </tr>
                          </thead>

                          <tbody>
                            {listExpnd.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="p20 taC fm-c fs13 fc666666">
                                  조회된 데이터가 없습니다.
                                </td>
                              </tr>
                            ) : (
                              listExpnd.map((row: any) => {
                                const rawId = row.atrzLinkDocId || row.ovrlMbsdsExpndDocId;
                                const id = String(rawId);
                                const isSelected = ovrlMbsdsExpndDocId === String(rawId);

                                return (
                                  <tr
                                    key={id}
                                    onClick={() => selectExpndRow(row)}
                                    className="transitionBgc"
                                    style={{ cursor: "pointer", backgroundColor: isSelected ? "#eaf1faea" : "transparent" }}
                                  >
                                    <td className="bd1 bdcEDEDED p10 taC">
                                      <input
                                        type="radio"
                                        name="expndRowSelect"
                                        checked={isSelected}
                                        onChange={() => selectExpndRow(row)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taC fm-c fs13 fc333333 wsNw">
                                      {Util.cvtDateStrForm(String(row.implDt), "-", "YYYYMMDD")}
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taL fm-c fs13 fc333333 wsNw ofH toE" title={String(row.bgtSbjct1stNm)}>
                                      {row.bgtSbjct1stNm}
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taL fm-c fs13 fc333333 wsNw ofH toE" title={String(row.bgtSbjct2stNm)}>
                                      {row.bgtSbjct2stNm}
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taL fm-c fs13 fc333333 wsNw ofH toE" title={String(row.bgtSbjct3stNm)}>
                                      {row.bgtSbjct3stNm}
                                    </td>
                                    <td className="bd1 bdcEDEDED p10 taR fm-c fs13 fc333333 wsNw">{Util.comma(String(row.implAmt))}</td>
                                    <td
                                      className="bd1 bdcEDEDED p10 taC fm-c fs13 wsNw"
                                      style={{
                                        color:
                                          String(row?.atrzLinkDocIdntySeCd) === "01"
                                            ? "#007bff"
                                            : String(row?.atrzLinkDocIdntySeCd) === "02"
                                            ? "#dc3545"
                                            : "#333333",
                                      }}
                                    >
                                      {row?.atrzLinkDocIdntySeNm}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  <div>
                    <table className="w100p bgcDCDCDC" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
                      <colgroup>
                        <col style={{ width: 54 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 90 }} />
                        <col style={{ width: 160 }} />
                        <col style={{ width: 139 }} />
                        <col style={{ width: 80 }} />
                      </colgroup>
                      <tbody>
                        <tr>
                          <td colSpan={5} className="taC">
                            <div className="h20 dpFlx aiE jcC pb10 pt5">
                              <span className="fm-c fs14 ls06 fc2c3e50">합계</span>
                            </div>
                          </td>
                          <td className="bd1 bdcEDEDED p10 taR fm-c fs13 fc333333 wsNw">
                            {mode === "DPST"
                              ? Util.isEmpty(sumDpst.sumDpstAmt)
                                ? ""
                                : Util.comma(String(sumDpst.sumDpstAmt))
                              : Util.isEmpty(sumExpnd.sumImplAmt)
                              ? ""
                              : Util.comma(String(sumExpnd.sumImplAmt))}
                          </td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AtrzDocBoxSumTab;
